/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';

import Modal from '../../Modal';
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { formatDateBr } from '../../../utils/formatDate';
import api from '../../../services/api';
import { EtanolChecklistReport } from '../../Reports/EtanolChecklistReport';

import { useForm } from 'react-hook-form';
import { MuiDatePicker } from '../../Material/MuiDatePicker';
import { VhpChecklistReport } from '../../Reports/VhpChecklistReport';

interface IModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'left' | 'center';
  format?: (value: string | boolean) => string;
}

const columns: Column[] = [
  {
    id: 'data',
    label: 'Data',
    minWidth: 170,
    align: 'center',
    format: (value: string | boolean) => formatDateBr(String(value)),
  },
  { id: 'nome', label: 'Motorista', minWidth: 100, align: 'center' },
  { id: 'produto', label: 'Produto', minWidth: 100, align: 'center' },
  { id: 'spot', label: 'Spot', minWidth: 100, align: 'center' },
  {
    id: 'placa',
    label: 'Placa',
    minWidth: 100,
    align: 'center',
  },
  {
    id: 'cpfmotorista',
    label: 'CPF',
    minWidth: 140,
    align: 'center',
  },
  {
    id: 'volume',
    label: 'Volume',
    minWidth: 140,
    align: 'center',
  },
  {
    id: 'checklistaprovado',
    label: 'Aprovado',
    minWidth: 170,
    align: 'center',
    format: (value: string | boolean) => (value ? 'Sim' : 'Não'),
  },
];

interface IFormData {
  queryDate: string;
}

const ModalPrintChecklist = React.forwardRef<HTMLInputElement, IModalProps>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ({ isOpen, setIsOpen }, ref) => {
    const [checklistData, setChecklistData] = useState<any[] | null>(null);

    const [reportData, setReportData] = useState<any>(null);

    const [loading, setLoading] = useState(false);

    const minWidthMuiInput = '400px';
    const maxWidthMuiInput = '400px';

    const { control, watch, reset } = useForm<IFormData>({
      defaultValues: {
        queryDate: dayjs().format('DD/MM/YYYY'),
      },
    });

    const queryDate = watch('queryDate');

    useEffect(() => {
      if (!isOpen || !queryDate) {
        return;
      }

      const loadCheckList = async () => {
        try {
          setLoading(true);

          const formattedDate = dayjs(queryDate, 'DD/MM/YYYY').format(
            'YYYY-MM-DD',
          );

          const [resultEtanol, resultVHP] = await Promise.all([
            api.get(`/checklist/etanol/bydata/${formattedDate}`),
            api.get(`/checklist/vhp/bydata/${formattedDate}`),
          ]);

          const checklistEtanol = resultEtanol.data.map((item: any) => ({
            ...item,
            checklistType: 'etanol' as const,
          }));

          const checklistVHP = resultVHP.data.map((item: any) => ({
            ...item,
            checklistType: 'vhp' as const,
          }));

          setChecklistData([...checklistEtanol, ...checklistVHP]);

          setLoading(false);
        } catch (error) {
          console.error('Error loading checklist:', error);
          setChecklistData([]);
          setLoading(false);
        }
      };

      loadCheckList();
    }, [isOpen, queryDate]);

    useEffect(() => {
      if (!reportData) return;

      // Wait until React renders EtanolChecklistReport into the DOM.
      const printTimer = window.setTimeout(() => {
        window.print();
      }, 100);

      return () => {
        window.clearTimeout(printTimer);
      };
    }, [reportData]);

    useEffect(() => {
      const handleAfterPrint = () => {
        setReportData(null);
      };

      window.addEventListener('afterprint', handleAfterPrint);

      return () => {
        window.removeEventListener('afterprint', handleAfterPrint);
      };
    }, []);

    function handleDoChecklist(checklist: any): void {
      setReportData(checklist);
    }

    return (
      <>
        <Modal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          size="1200px"
          onCloseModal={() => reset()}
        >
          <div style={{ position: 'relative' }}>
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 10,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '16px',
                  backgroundColor: 'rgba(255, 255, 255, 0.85)',
                  borderRadius: '4px',
                }}
              >
                <CircularProgress sx={{ color: '#00a859' }} />

                <span
                  style={{
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#171412',
                  }}
                >
                  Carregando...
                </span>
              </div>
            )}
            <h1
              style={{
                color: '#00a859',
                textAlign: 'center',
                marginBottom: '20px',
              }}
            >
              Lista Re-impressão checklist
            </h1>

            <h6
              style={{
                textAlign: 'center',
                marginBottom: '20px',
                opacity: 0.7,
              }}
            >
              Clique em um agendamento da tabela abaixo para imprimir o
              checklist
            </h6>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                padding: '1rem 0',
              }}
            >
              <MuiDatePicker
                name={'queryDate'}
                label={'Selecione a data'}
                control={control}
                minWidth={minWidthMuiInput}
                maxWidth={maxWidthMuiInput}
              />
            </div>

            <Paper sx={{ width: '100%', overflow: 'auto' }}>
              <TableContainer sx={{ maxHeight: 640, padding: 2 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns.map(column => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            // top: 57,
                            minWidth: column.minWidth,
                          }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {checklistData && checklistData.length > 0 ? (
                      checklistData.map(checklist => {
                        return (
                          <TableRow
                            key={`${checklist.checklistType}-${checklist.id}`}
                            sx={{
                              cursor: 'pointer',
                            }}
                            hover
                            role="checkbox"
                            tabIndex={-1}
                            onClick={() => {
                              handleDoChecklist(checklist);
                            }}
                          >
                            {columns.map(column => {
                              const value =
                                column.id === 'nome' &&
                                checklist?.checklistType === 'vhp'
                                  ? checklist['motorista']
                                  : checklist[column.id];

                              return (
                                <TableCell key={column.id} align={column.align}>
                                  {column.format ? column.format(value) : value}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          Nenhum agendamento encontrado.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </div>
        </Modal>

        {reportData?.checklistType === 'etanol' && (
          <EtanolChecklistReport reportData={reportData} />
        )}

        {reportData?.checklistType === 'vhp' && (
          <VhpChecklistReport reportData={reportData} />
        )}
      </>
    );
  },
);

ModalPrintChecklist.displayName = 'ModalPrintChecklist';

export default ModalPrintChecklist;
